import { ApiResponse } from '@shared/types';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { downloadPdfFromBase64 } from '../util/pdfDownload';
import { supabase } from './supabase';

// Edge functions return `{ error }` with a non-2xx status; supabase-js only
// exposes a generic message unless we read the response body ourselves.
const readFunctionError = async (
  error: unknown,
  fallback: string,
): Promise<string> => {
  if (error instanceof FunctionsHttpError) {
    try {
      const payload = await error.context.json();
      return payload?.error || payload?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return error instanceof Error ? error.message || fallback : fallback;
};

/**
 * Generates an A4 PDF of a single bill server-side and saves it. The anchor
 * download opens Electron's native save dialog, so web and desktop share
 * this path.
 */
export const exportBillPdf = async (
  orderId: string,
): Promise<ApiResponse<{ filename: string }>> => {
  const { data, error } = await supabase.functions.invoke('export-bill-pdf', {
    body: { orderId },
  });

  if (error || !data?.pdfBase64) {
    return {
      success: false,
      error: await readFunctionError(error, 'Failed to generate the bill PDF'),
    };
  }

  downloadPdfFromBase64(data.pdfBase64, data.filename);
  return { success: true, data: { filename: data.filename } };
};

export const emailBill = async (
  orderId: string,
  recipients: string[],
): Promise<ApiResponse<{ recipients: string[] }>> => {
  const { data, error } = await supabase.functions.invoke('send-bill-email', {
    body: { orderId, recipients },
  });

  if (error || !data?.success) {
    return {
      success: false,
      error: await readFunctionError(error, 'Failed to email the bill'),
    };
  }

  return { success: true, data: { recipients: data.recipients ?? recipients } };
};
